'use client';

import { type MouseEvent, memo, useCallback, useRef, useState } from 'react';

import { ClipType } from '@air/api/types';
import { FileVideoCameraIcon, LayersIcon } from '@air/design-icons';

import { GalleryAssetCardBadge } from '~/components/gallery/GalleryAssetCardBadge';
import { VideoDurationBadge } from '~/components/gallery/VideoDurationBadge';
import { ListItemRowBase } from '~/components/list/ListItemRowBase';
import { ListItemRowThumbnail } from '~/components/list/ListItemRowThumbnail';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { type ListViewColumnField } from '~/constants/listViewColumns';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import { type ClipListViewItem } from '~/utils/formatItemsListResponse';
import { imgixLoader } from '~/utils/imgixLoader';
import { getClipListViewRowInfo } from '~/utils/listViewRowInfo';

export type ClipListItemRowProps = {
  isSelected?: boolean;
  item: ClipListViewItem;
  onClick?: (item: ClipListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: (item: ClipListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  visibleColumns: ListViewColumnField[];
};

const THUMBNAIL_SIZE = 32;
const SPRITESHEET_REQUEST_WIDTH = THUMBNAIL_SIZE * 10;

export const ClipListItemRow = memo(
  ({ isSelected, item, onClick, onDoubleClick, visibleColumns }: ClipListItemRowProps) => {
    const title = item.clip.title ?? item.clip.importedName ?? 'Untitled';
    const thumbnailUrl = item.clip.assets?.image ?? item.clip.assets?.original;
    const isVideo = item.clip.type === ClipType.video;
    const hasMultipleVersions = (item.clip.version ?? 0) > 1;
    const testId = `list-asset-${item.id}`;

    const thumbnailRef = useRef<HTMLDivElement>(null);
    const [isThumbnailHovered, setIsThumbnailHovered] = useState(false);

    const spriteSheet = item.clip.assets?.spriteSheet;
    const isScrubbable = isVideo && !!spriteSheet?.src;

    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
      useVideoScrubbableThumbnail({
        containerDimensions: { height: THUMBNAIL_SIZE, width: THUMBNAIL_SIZE },
        scrubRegionRef: thumbnailRef,
        spritesheet: spriteSheet,
      });

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClick?.(item, event);
      },
      [item, onClick],
    );

    const handleDoubleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onDoubleClick?.(item, event);
      },
      [item, onDoubleClick],
    );

    const handleMouseMove = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (!isScrubbable) {
          return;
        }

        const rect = thumbnailRef.current?.getBoundingClientRect();

        if (!rect) {
          return;
        }

        const isOverThumbnail =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        setIsThumbnailHovered(isOverThumbnail);

        if (isOverThumbnail) {
          onMouseMove(event);
        }
      },
      [isScrubbable, onMouseMove],
    );

    const handleRowMouseLeave = useCallback(() => {
      setIsThumbnailHovered(false);
      onMouseLeave();
    }, [onMouseLeave]);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: mouse handlers drive the thumbnail hover scrub only; the row's interactive element is the inner overlay button
      <div onMouseLeave={handleRowMouseLeave} onMouseMove={handleMouseMove}>
        <ListItemRowBase
          actionTestId={`${testId}-action`}
          isSelected={isSelected}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          rowInfo={getClipListViewRowInfo(item.clip)}
          testId={testId}
          thumbnail={
            <div className="relative shrink-0" ref={thumbnailRef}>
              <ListItemRowThumbnail alt={title} src={thumbnailUrl} />
              {isThumbnailHovered && spriteSheet?.src && (
                <VideoScrubbableThumbnail
                  backgroundImageRef={backgroundImageRef}
                  indicatorRef={indicatorRef}
                  src={imgixLoader({ src: spriteSheet.src, width: SPRITESHEET_REQUEST_WIDTH })}
                  style={backgroundImageStyles}
                />
              )}
              {!isThumbnailHovered && <VideoDurationBadge className="absolute right-0.5 bottom-0.5" clip={item.clip} />}
              {(isVideo || hasMultipleVersions) && (
                <div className="pointer-events-none absolute -top-1 -left-1 flex gap-0.5">
                  {isVideo && (
                    <GalleryAssetCardBadge>
                      <FileVideoCameraIcon size="xs" />
                    </GalleryAssetCardBadge>
                  )}
                  {hasMultipleVersions && (
                    <GalleryAssetCardBadge>
                      <LayersIcon size="xs" />
                    </GalleryAssetCardBadge>
                  )}
                </div>
              )}
            </div>
          }
          title={title}
          visibleColumns={visibleColumns}
        />
      </div>
    );
  },
);

ClipListItemRow.displayName = 'ClipListItemRow';
